import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Save, 
  Loader2,
  FileText,
  RefreshCw
} from 'lucide-react';
import { getAllContent, updateContent } from '@/lib/api';
import { toast } from 'sonner';

const AdminContent = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const contentFields = [
    { key: 'hero_title', label: 'Hero Başlık', type: 'input' },
    { key: 'hero_subtitle', label: 'Hero Alt Başlık', type: 'textarea' },
    { key: 'about_title', label: 'Hakkımızda Başlık', type: 'input' },
    { key: 'about_content', label: 'Hakkımızda İçerik', type: 'textarea' },
  ];

  const fetchContent = async () => {
    try {
      const response = await getAllContent();
      const contentMap = {};
      response.data.forEach(item => {
        contentMap[item.key] = item.content || '';
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('İçerik yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    
    try {
      await updateContent(key, { content: content[key] });
      toast.success('İçerik başarıyla güncellendi');
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error('İçerik güncellenirken hata oluştu');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-content">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              İçerik Yönetimi
            </h2>
            <p className="text-slate-600">Site içeriklerini düzenleyin</p>
          </div>
          <Button 
            variant="outline"
            onClick={fetchContent}
            data-testid="refresh-content-btn"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Yenile
          </Button>
        </div>

        {/* Content Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
          </div>
        ) : (
          <div className="grid gap-6">
            {contentFields.map((field, index) => (
              <Card 
                key={field.key}
                data-testid={`content-card-${index}`}
                className="border-slate-200"
              >
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-[#005CB2]" />
                    {field.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor={field.key}>İçerik</Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={field.key}
                        data-testid={`content-${field.key}`}
                        value={content[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        rows={4}
                        placeholder={`${field.label} girin...`}
                      />
                    ) : (
                      <Input
                        id={field.key}
                        data-testid={`content-${field.key}`}
                        value={content[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        placeholder={`${field.label} girin...`}
                      />
                    )}
                  </div>
                  <Button 
                    onClick={() => handleSave(field.key)}
                    data-testid={`save-${field.key}`}
                    disabled={saving[field.key]}
                    className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                  >
                    {saving[field.key] ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Kaydet
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-800 mb-1">Bilgi</h3>
                <p className="text-sm text-blue-700">
                  Bu sayfada site üzerindeki temel içerikleri düzenleyebilirsiniz. 
                  Yapılan değişiklikler kaydedildikten sonra site üzerinde otomatik olarak güncellenir.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminContent;
